import { useState, useCallback } from 'react';
import { getLoggerSafe } from '@vitvio/logging';

type StorageType = 'localStorage' | 'sessionStorage';

export function usePersistentState<T>(initialValue: T, persistenceKey: string, storage: StorageType = 'localStorage') {
  const logger = getLoggerSafe();
  const storageObj = storage === 'localStorage' ? localStorage : sessionStorage;

  const getPersistedState = useCallback(
    (defaultValue: T) => {
      if (persistenceKey in storageObj) {
        const storedValue = storageObj.getItem(persistenceKey);
        if (storedValue) {
          try {
            const parsedValue = JSON.parse(storedValue);
            return parsedValue;
          } catch (error) {
            logger?.error(error as Error, { context: 'usePersistentState', persistenceKey, storage });
          }
        }
      }
      return defaultValue;
    },
    [persistenceKey, storage, logger],
  );

  const [localState, setLocalState] = useState<T>(() => {
    const initial = getPersistedState(initialValue);
    return initial;
  });

  const setValue = useCallback(
    (newValue: T) => {
      setLocalState(newValue);
      storageObj.setItem(persistenceKey, JSON.stringify(newValue));
    },
    [storage, persistenceKey],
  );

  return [localState, setValue] as const;
}
